"use client";

import React from "react";
import { motion, useTransform, type MotionValue } from "framer-motion";
import { cn } from "@/src/lib/utils";
import { useSwipeBack } from "../hooks/useSwipeBack";

interface IPushedPageProps {
  title: string;
  /** 0 = on screen, 1 = off to the right; the opener animates it with `IOS_PUSH`. */
  progress: MotionValue<number>;
  /** The back button: the opener slides the page out. */
  onBack: () => void;
  /** After a swipe has already slid the page out. */
  onSwipedBack: () => void;
  footer?: React.ReactNode;
  /** Overrides for the footer's frame (border colour, padding). */
  footerClassName?: string;
  children: React.ReactNode;
}

/**
 * A page pushed over the one below it, as on iPhone: it covers its parent (`absolute
 * inset-0`), slides with `progress`, and dragging it right goes back. The contact flow's
 * date and guest pickers.
 */
const PushedPage: React.FC<IPushedPageProps> = ({
  title,
  progress,
  onBack,
  onSwipedBack,
  footer,
  footerClassName,
  children,
}) => {
  const x = useTransform(progress, (v) => `${v * 100}%`);
  const swipe = useSwipeBack(progress, onSwipedBack);
  // This page sits inside another swipeable page: its drags are its own.
  const own =
    (handler: (e: React.PointerEvent<HTMLElement>) => void) =>
    (e: React.PointerEvent<HTMLElement>) => {
      e.stopPropagation();
      handler(e);
    };

  return (
    <motion.div
      role="group"
      aria-label={title}
      style={{ x }}
      onPointerDown={own(swipe.onPointerDown)}
      onPointerMove={own(swipe.onPointerMove)}
      onPointerUp={own(swipe.onPointerUp)}
      onPointerCancel={own(swipe.onPointerCancel)}
      className="absolute inset-0 z-20 flex touch-pan-y flex-col bg-white pt-[env(safe-area-inset-top)] shadow-[-8px_0_24px_rgba(0,0,0,0.08)] md:pt-0"
    >
      {/* The app's `CustomAppbarView`: 56px, the title centred in 18px semibold black. */}
      <div className="relative flex h-14 shrink-0 items-center justify-center px-6 md:mt-2">
        <button
          type="button"
          onClick={onBack}
          aria-label="Back"
          className="absolute left-4 flex h-10 w-10 items-center justify-center"
        >
          <img
            src="/icons/listing/listing_page_back.svg"
            alt=""
            className="h-6 w-6"
          />
        </button>
        <h3 className="text-18 font-semibold text-black">{title}</h3>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-6">{children}</div>
      {footer && (
        <div
          className={cn(
            "border-t border-[#EBEBEB] px-4 pt-4 pb-[max(1rem,env(safe-area-inset-bottom))]",
            footerClassName,
          )}
        >
          {footer}
        </div>
      )}
    </motion.div>
  );
};

export default PushedPage;
